// Site layout — title + tabs persist across /floors, /logical-groups
// child routes. Mirrors TenantLayout's shape.

import { useQuery } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";

import { sites } from "../../api/endpoints/sites";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Pill } from "../../components/ui/Pill";
import { PageHeader } from "../../components/shell/PageHeader";
import { LayoutImportButton } from "../layoutImport/LayoutImportButton";

interface SiteLayoutProps {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
}

export function SiteLayout({
  accountSlug,
  tenantSlug,
  siteSlug,
}: SiteLayoutProps) {
  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.site(accountSlug, tenantSlug, siteSlug),
    queryFn: () =>
      sites.get(accountSlug, tenantSlug, siteSlug).then((r) => r.data),
  });

  return (
    <>
      <PageHeader
        title={q.data?.display_name ?? siteSlug}
        subtitle={
          q.data ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <code>{q.data.slug}</code>
              {q.data.ha_instance_id ? (
                <Pill tone="ok">HA bound</Pill>
              ) : (
                <Pill tone="neutral">HA unbound</Pill>
              )}
            </span>
          ) : (
            siteSlug
          )
        }
        actions={
          <LayoutImportButton
            accountSlug={accountSlug}
            tenantSlug={tenantSlug}
            siteSlug={siteSlug}
          />
        }
      />

      {q.error && (
        <div style={{ marginBottom: 16 }}>
          <ErrorBanner error={q.error} title="Failed to load site" />
        </div>
      )}

      <nav style={tabStrip} aria-label="Site sub-resources">
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug"
          params={{ accountSlug, tenantSlug, siteSlug }}
          activeOptions={{ exact: true }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Overview
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug/areas"
          params={{ accountSlug, tenantSlug, siteSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Areas
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug/devices"
          params={{ accountSlug, tenantSlug, siteSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Devices
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug/logical-groups"
          params={{ accountSlug, tenantSlug, siteSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Logical Groups
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug/network"
          params={{ accountSlug, tenantSlug, siteSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Network
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug/access"
          params={{ accountSlug, tenantSlug, siteSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Access
        </Link>
        <Link
          to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug/hosts"
          params={{ accountSlug, tenantSlug, siteSlug }}
          style={tabStyle}
          activeProps={{ style: activeTabStyle }}
        >
          Hosts
        </Link>
      </nav>

      <Outlet />
    </>
  );
}

const tabStrip: React.CSSProperties = {
  display: "flex",
  gap: 4,
  marginBottom: 16,
  borderBottom: "1px solid var(--border)",
};

const tabStyle: React.CSSProperties = {
  padding: "6px 12px",
  fontSize: "0.85rem",
  color: "var(--text)",
  textDecoration: "none",
  borderBottom: "2px solid transparent",
};

const activeTabStyle: React.CSSProperties = {
  ...tabStyle,
  color: "var(--accent)",
  borderBottomColor: "var(--accent)",
  fontWeight: 600,
};
